import { EditOutlined, ReloadOutlined, StopOutlined, UndoOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, MaterialIcon } from '@gravitate-js/excalibrr'
import type { GridApi } from 'ag-grid-community'
import { Popconfirm, Tooltip } from 'antd'
import type React from 'react'
import { useState } from 'react'

import type { InvoiceDetail } from '../../../../../api/useFreight/types'
import { useLineItemManagement } from '../../../shared'
import { useTransactions } from '@/api/useFreight/useTransactions'
import { AddFreightLineItemPopover } from '../AddFreightLineItem'

interface ActionsCellRendererProps {
  data: InvoiceDetail
  api: GridApi
  context?: {
    scopes: Gravitate.AuthorizationResponse
    rebillUpdateActions?: {
      overrideRebillLineItem: (
        detail: InvoiceDetail,
        amountOverride: number | null,
        totalOverride: number | null
      ) => void
    }
    hideActions?: boolean
  }
  isCreditRebillMode?: boolean
}

export const ActionsCellRenderer: React.FC<ActionsCellRendererProps> = ({ data, api, context, isCreditRebillMode }) => {
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)

  const rowId = data.id
  const isNeedsApproval = data.status === 'needs_approval'
  const bookType = data.book_type

  const canApprove = context?.scopes?.freight?.approve
  const canEdit = (data.status !== 'posted' && data.status !== 'estimated') || context?.rebillUpdateActions

  const { handleApprove, handleReject } = useLineItemManagement({
    api,
    data,
  })
  const { useResetTransactionMutation } = useTransactions()

  const handleApproveClick = async () => {
    if (!bookType) {
      return
    }

    setIsApproving(true)
    await handleApprove(rowId, bookType, data.customer_id || undefined)
    setIsApproving(false)
  }

  const handleRejectClick = async () => {
    if (!bookType) {
      return
    }

    setIsRejecting(true)
    await handleReject(rowId, bookType)
    setIsRejecting(false)
  }

  function handleReset() {
    useResetTransactionMutation.mutateAsync({
      body: {
        transaction_id: data.id,
        book_type: data.book_type,
        customer_id: data.customer_id,
      },
    })
  }

  // If hideActions is true, don't render edit buttons
  if (context?.hideActions) {
    return <div />
  }

  return (
    <Horizontal flex={1} width={'auto'} gap={4} verticalCenter style={{ justifyContent: 'flex-end' }} className='px-2'>
      {isNeedsApproval && canApprove && (
        <>
          <Tooltip title='Approve'>
            <GraviButton
              color='primary'
              size='small'
              icon={<MaterialIcon icon='check' style={{ display: 'flex' }} />}
              loading={isApproving}
              disabled={isApproving || isRejecting}
              onClick={handleApproveClick}
            />
          </Tooltip>
          <Tooltip title='Reject'>
            <Popconfirm
              title='Reject this line item?'
              onConfirm={handleRejectClick}
              okText='Yes'
              cancelText='No'
              trigger={'click'}
            >
              <GraviButton
                size='small'
                error
                icon={<StopOutlined style={{ display: 'flex' }} />}
                loading={isRejecting}
                disabled={isApproving || isRejecting}
              />
            </Popconfirm>
          </Tooltip>
        </>
      )}

      <Tooltip title={canEdit ? 'Edit' : 'Unable to edit while status is Posted, Estimated, or awaiting CR approval'}>
        <div style={{ display: 'inline-block' }}>
          <AddFreightLineItemPopover
            actionType='edit'
            gridApi={api}
            fliDetail={data}
            context={context}
            isCreditRebillMode={isCreditRebillMode}
          >
            <GraviButton size='small' icon={<EditOutlined style={{ display: 'flex' }} />} disabled={!canEdit} />
          </AddFreightLineItemPopover>
        </div>
      </Tooltip>

      <Tooltip title={canEdit ? 'Reset' : 'Unable to reset while status is Posted or Estimated'}>
        <GraviButton
          size='small'
          icon={<MaterialIcon icon='undo' size={14} style={{ display: 'flex' }} />}
          loading={useResetTransactionMutation.isPending}
          disabled={useResetTransactionMutation.isPending || !data.has_override || !canEdit}
          onClick={handleReset}
        />
      </Tooltip>
    </Horizontal>
  )
}
